import clsx from 'clsx'
import { useNavigation } from 'expo-router'
import { useState } from 'react'
import { View, type ViewProps } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Candle } from '@/src/elements/candle'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TWatchmanDailyIntentionApi } from '../../../api/types/getWatchmanIntentionsApi.types'
import { INTENTION_TYPE } from '../../../models/Intention.enums'

const BIRTHDAY_EMOJI = '🎂'

type TIntentionsPrayFlowPrayStepProps = {
  dailyIntention: TWatchmanDailyIntentionApi
  onPrayed: () => Promise<unknown>
  onAmen: VoidFunction
  isLoading: boolean
  paused?: boolean
} & Pick<ViewProps, 'style'>

const AVERAGE_TEXT_HEIGHT = 50

export const IntentionsPrayFlowPrayStep = ({
  dailyIntention,
  onPrayed,
  onAmen,
  isLoading,
  paused,
  style,
}: TIntentionsPrayFlowPrayStepProps) => {
  const [prayStatus, setPrayStatus] = useState<'pending' | 'praying' | 'submitting' | 'finished'>('pending')
  const [containerHeight, setContainerHeight] = useState(0)
  const { t } = useTranslate()
  const isFocused = useNavigation().isFocused()

  const handleCandleFinish = () => {
    if (prayStatus !== 'praying') return
    setPrayStatus('submitting')
    onPrayed()
      .then(() => setPrayStatus('finished'))
      .catch(() => setPrayStatus('pending'))
  }

  const onStartPray = () => {
    setPrayStatus('praying')
  }

  const user = dailyIntention.intention.user
  const birthdayDate = user && 'birthdate' in user ? user.birthdate : undefined
  const userName = user?.name || ''

  return (
    <View style={style} className="px-4 pb-6 justify-center items-center native:h-full">
      <View className="web:mx-auto w-full max-w-lg gap-3 py-4 grow">
        <View
          onLayout={(e) => {
            setContainerHeight(e.nativeEvent.layout.height)
          }}
          className={'transition-height duration-700 gap-4 p-2 native:h-full'}
        >
          <View
            className={'transition-transform duration-700 gap-3'}
            style={{
              transform:
                prayStatus === 'pending' && containerHeight > 0
                  ? [{ translateY: containerHeight / 2 - AVERAGE_TEXT_HEIGHT }]
                  : [],
            }}
          >
            <Typography
              bold
              colors="primary"
              contrast={dailyIntention.intentionType === INTENTION_TYPE.INTENTION ? 'high' : 'default'}
              size="lg"
            >
              {dailyIntention.intentionType === INTENTION_TYPE.INTENTION && userName}
              {dailyIntention.intentionType === INTENTION_TYPE.ARRIVAL &&
                t('intention.watchman.arrival.just-joined', { userName })}
              {dailyIntention.intentionType === INTENTION_TYPE.BIRTHDAY &&
                t('pray.intention.birthday.title', { userName })}
            </Typography>
            {dailyIntention.intentionType === INTENTION_TYPE.BIRTHDAY && birthdayDate && (
              <View className="flex-row items-baseline py-1">
                <Typography size="3xl" className="pr-3" role="img" aria-label={t('user.happy-birthday.wish')}>
                  {BIRTHDAY_EMOJI}
                </Typography>
                <Typography bold colors="primary" size="lg">
                  {t('intention.birthday.years-old', {
                    age: new Date(Date.now()).getFullYear() - new Date(birthdayDate).getFullYear(),
                  })}
                </Typography>
              </View>
            )}
            {dailyIntention.intentionType === INTENTION_TYPE.INTENTION && 'content' in dailyIntention.intention && (
              <Typography colors="primary" contrast="high">
                {dailyIntention.intention.content}
              </Typography>
            )}
          </View>
          <View
            className={clsx(
              'web:self-center grow items-center justify-center transition-opacity duration-700',
              prayStatus === 'pending' ? 'opacity-0 pointer-events-none' : 'opacity-100',
            )}
          >
            <Candle
              onFinish={handleCandleFinish}
              enabled={isFocused && !isLoading && !paused && prayStatus !== 'pending'}
              className="bg-primary-bg-light"
            />
          </View>
        </View>
      </View>
      <Button
        variant="solid"
        colors="primary"
        fullWidth={prayStatus === 'pending'}
        isDisabled={prayStatus === 'praying' || prayStatus === 'submitting' || isLoading}
        onPress={prayStatus === 'pending' ? onStartPray : onAmen}
        testID={
          prayStatus === 'pending' ? TEST_ID.WATCHMAN.INTENTIONS.PRAY_BUTTON : TEST_ID.WATCHMAN.INTENTIONS.AMEN_BUTTON
        }
      >
        {prayStatus === 'pending' ? t('pray.pray') : t('pray.amen')}
      </Button>
    </View>
  )
}
